import Phaser from 'phaser';
import { COLORS, GAME_W, GAME_H, intToHex } from '../utils/constants';
import { weaponSystem, WEAPONS, Weapon } from '../systems/WeaponSystem';
import { walletManager } from '../systems/WalletManager';
import { soundManager } from '../systems/SoundManager';

export default class ShopScene extends Phaser.Scene {
  private coinsText!: Phaser.GameObjects.Text;
  private gemsText!: Phaser.GameObjects.Text;
  private items: Phaser.GameObjects.Container[] = [];
  private selectedIndex = -1;
  private detailPanel!: Phaser.GameObjects.Container;

  constructor() { super({ key: 'ShopScene' }); }

  create() {
    this.cameras.main.setBackgroundColor(COLORS.bgDark);

    const grid = this.add.graphics().setDepth(0);
    grid.lineStyle(1, COLORS.purple, 0.03);
    for (let x = 0; x < GAME_W; x += 40) grid.lineBetween(x, 0, x, GAME_H);
    for (let y = 0; y < GAME_H; y += 40) grid.lineBetween(0, y, GAME_W, y);

    // 商城粒子背景
    if (this.textures.exists('glow')) {
      this.add.particles(GAME_W / 2, GAME_H / 2, 'glow', {
        lifespan: 4000,
        speed: { min: 10, max: 40 },
        angle: { min: 0, max: 360 },
        scale: { start: 0.3, end: 0 },
        alpha: { start: 0.3, end: 0 },
        blendMode: Phaser.BlendModes.ADD,
        quantity: 1,
        tint: [COLORS.cyan, COLORS.purple, COLORS.yellow],
      }).setDepth(0);
    }

    this.add.text(GAME_W / 2, 36, '商城', {
      fontFamily: 'Microsoft YaHei, Arial, sans-serif',
      fontSize: '36px', color: intToHex(COLORS.cyan), fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(10);

    const coinIcon = this.add.text(GAME_W / 2 - 120, 80, '💰', {
      fontFamily: 'Arial', fontSize: '20px',
    }).setOrigin(0.5).setDepth(10);
    this.coinsText = this.add.text(GAME_W / 2 - 90, 80, String(walletManager.getCoins()), {
      fontFamily: 'Arial', fontSize: '18px', color: '#ffea00', fontStyle: 'bold',
    }).setOrigin(0, 0.5).setDepth(10);

    const gemIcon = this.add.text(GAME_W / 2 + 40, 80, '💎', {
      fontFamily: 'Arial', fontSize: '20px',
    }).setOrigin(0.5).setDepth(10);
    this.gemsText = this.add.text(GAME_W / 2 + 70, 80, String(walletManager.getGems()), {
      fontFamily: 'Arial', fontSize: '18px', color: intToHex(COLORS.cyan), fontStyle: 'bold',
    }).setOrigin(0, 0.5).setDepth(10);

    this.renderWeaponGrid();

    this.detailPanel = this.createDetailPanel();

    const backBtn = this.add.container(80, GAME_H - 40, [
      this.add.rectangle(0, 0, 160, 40, COLORS.purple).setOrigin(0.5).setAlpha(0.2),
      this.add.rectangle(0, 0, 160, 40, 0x000000).setOrigin(0.5).setStrokeStyle(2, COLORS.purple),
      this.add.text(0, 0, '返回', {
        fontFamily: 'Microsoft YaHei, sans-serif', fontSize: '16px', color: '#9945ff', fontStyle: 'bold',
      }).setOrigin(0.5),
    ]).setDepth(10);
    backBtn.setSize(160, 40).setInteractive({ useHandCursor: true });
    backBtn.on('pointerover', () => (backBtn.list[0] as Phaser.GameObjects.Rectangle).setAlpha(0.4));
    backBtn.on('pointerout', () => (backBtn.list[0] as Phaser.GameObjects.Rectangle).setAlpha(0.2));
    backBtn.on('pointerdown', () => {
      soundManager.play('ui_click');
      this.cameras.main.fadeOut(500, 0, 0, 0);
      this.time.delayedCall(500, () => this.scene.start('TitleScene'));
    });

    this.input.keyboard!.on('keydown-ESC', () => {
      this.cameras.main.fadeOut(500, 0, 0, 0);
      this.time.delayedCall(500, () => this.scene.start('TitleScene'));
    });
  }

  private renderWeaponGrid() {
    this.items.forEach(i => i.destroy());
    this.items = [];

    const cols = 4;
    const cellW = 260;
    const cellH = 100;
    const startX = (GAME_W - cols * cellW) / 2 + cellW / 2;
    const startY = 150;
    const allWeapons = weaponSystem.getAllWeapons();

    allWeapons.forEach((weapon, i) => {
      const col = i % cols;
      const row = Math.floor(i / cols);
      const x = startX + col * cellW;
      const y = startY + row * (cellH + 12);

      const equipped = weaponSystem.getEquipped().id === weapon.id;
      const owned = weapon.owned;

      const tierBorder: Record<string, number> = {
        common: COLORS.gray, rare: COLORS.cyan, epic: COLORS.purple, legendary: COLORS.yellow,
      };
      const border = equipped ? COLORS.green : owned ? tierBorder[weapon.tier] : COLORS.darkGray;

      const bg = this.add.rectangle(x, y, cellW - 12, cellH - 6, COLORS.bgPanel, owned ? 0.9 : 0.5)
        .setStrokeStyle(equipped ? 3 : 1, border);
      const icon = this.add.text(x - cellW / 2 + 30, y, weapon.icon, {
        fontFamily: 'Arial', fontSize: '28px',
      }).setOrigin(0.5).setDepth(11);
      const name = this.add.text(x - cellW / 2 + 58, y - 16, weapon.name, {
        fontFamily: 'Microsoft YaHei, sans-serif', fontSize: '14px',
        color: equipped ? '#00e676' : owned ? '#ffffff' : '#555577',
        fontStyle: 'bold',
      }).setOrigin(0, 0.5).setDepth(11);
      const tierLabel = this.add.text(x - cellW / 2 + 58, y + 4, weapon.tier.toUpperCase(), {
        fontFamily: 'Arial', fontSize: '10px',
        color: intToHex(tierBorder[weapon.tier]),
      }).setOrigin(0, 0.5).setDepth(11);
      const dmgText = this.add.text(x - cellW / 2 + 58, y + 20, `DMG: ${weapon.damage}  |  CD: ${weapon.cooldown}ms`, {
        fontFamily: 'Arial', fontSize: '10px', color: '#555577',
      }).setOrigin(0, 0.5).setDepth(11);

      let statusText: Phaser.GameObjects.Text;
      if (equipped) {
        statusText = this.add.text(x + cellW / 2 - 18, y, '装备中', {
          fontFamily: 'Microsoft YaHei, sans-serif', fontSize: '12px', color: '#00e676', fontStyle: 'bold',
        }).setOrigin(1, 0.5).setDepth(11);
      } else if (owned) {
        statusText = this.add.text(x + cellW / 2 - 18, y, '装备', {
          fontFamily: 'Microsoft YaHei, sans-serif', fontSize: '12px', color: intToHex(COLORS.cyan),
        }).setOrigin(1, 0.5).setDepth(11).setInteractive({ useHandCursor: true });
        statusText.on('pointerdown', (ptr: Phaser.Input.Pointer, event: any) => {
          event.stopPropagation();
          weaponSystem.equipWeapon(weapon.id);
          soundManager.play('ui_click');
          this.cameras.main.flash(200, 0, 240, 255);
          this.renderWeaponGrid();
          this.updateDetail(weapon);
        });
      } else {
        statusText = this.add.text(x + cellW / 2 - 18, y, `${weapon.price} 💰`, {
          fontFamily: 'Arial, sans-serif', fontSize: '12px', color: '#ffea00',
        }).setOrigin(1, 0.5).setDepth(11).setInteractive({ useHandCursor: true });
        statusText.on('pointerdown', (ptr: Phaser.Input.Pointer, event: any) => {
          event.stopPropagation();
          this.buyWeapon(weapon);
        });
      }

      const hitArea = this.add.rectangle(x, y, cellW - 12, cellH - 6, 0x000000, 0)
        .setInteractive({ useHandCursor: true }).setDepth(12);
      hitArea.on('pointerdown', () => {
        this.selectedIndex = i;
        this.updateDetail(weapon);
      });

      const container = this.add.container(0, 0, [bg, icon, name, tierLabel, dmgText, statusText, hitArea]).setDepth(10);
      this.items.push(container);
    });
  }

  private createDetailPanel(): Phaser.GameObjects.Container {
    const x = GAME_W / 2;
    const y = GAME_H - 120;
    const pw = 600;
    const ph = 140;

    const bg = this.add.rectangle(x, y, pw, ph, 0x0a0a1a, 0.95)
      .setStrokeStyle(1, COLORS.cyan, 0.5).setDepth(10);
    const descText = this.add.text(x - pw / 2 + 20, y - 30, '选择一把武器查看详情', {
      fontFamily: 'Microsoft YaHei, sans-serif', fontSize: '14px', color: '#555577',
    }).setOrigin(0, 0).setDepth(11);
    const statsText = this.add.text(x - pw / 2 + 20, y + 5, '', {
      fontFamily: 'Microsoft YaHei, sans-serif', fontSize: '12px', color: '#aaaaaa',
    }).setOrigin(0, 0).setDepth(11);

    return this.add.container(0, 0, [bg, descText, statsText]).setDepth(10);
  }

  private updateDetail(weapon: Weapon) {
    const bg = this.detailPanel.list[0] as Phaser.GameObjects.Rectangle;
    const descText = this.detailPanel.list[1] as Phaser.GameObjects.Text;
    const statsText = this.detailPanel.list[2] as Phaser.GameObjects.Text;

    descText.setText(`${weapon.icon} ${weapon.name}  —  ${weapon.description}`);
    descText.setColor(weapon.owned ? '#ffffff' : '#555577');
    statsText.setText(
      `伤害: ${weapon.damage}  |  冷却: ${weapon.cooldown}ms  |  范围: ${weapon.range}  |  速度倍率: ${weapon.speed}x  |  等级: ${weapon.tier.toUpperCase()}`
    );
  }

  private buyWeapon(weapon: Weapon) {
    if (walletManager.getCoins() < weapon.price) {
      this.showMessage('金币不足!', '#ff1744');
      return;
    }
    walletManager.spendCoins(weapon.price);
    weaponSystem.buyWeapon(weapon.id);
    soundManager.play('challenge_success');
    this.coinsText.setText(String(walletManager.getCoins()));
    this.gemsText.setText(String(walletManager.getGems()));
    this.renderWeaponGrid();
    this.updateDetail(weapon);
    this.showMessage(`购买成功: ${weapon.name}`, '#00e676');
  }

  private showMessage(text: string, color: string) {
    const msg = this.add.text(GAME_W / 2, GAME_H / 2, text, {
      fontFamily: 'Microsoft YaHei, sans-serif', fontSize: '24px',
      color, fontStyle: 'bold',
      stroke: '#000000', strokeThickness: 3,
    }).setOrigin(0.5).setDepth(100).setAlpha(0);
    this.tweens.add({
      targets: msg, alpha: 1, y: GAME_H / 2 - 40, duration: 300,
      yoyo: true, hold: 800, onComplete: () => msg.destroy(),
    });
  }
}
